import React, { useState, useEffect, useCallback } from 'react';
import {
    Typography,
    Paper,
    Button,
    Switch,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useParams } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    getAdminArticleById,
    deleteAdminArticle,
    writeAdminArticleStream,
    getAdminEditorialAreas,
    getNeoFeedContextSuggestions,
} from '../../../utils/api';
import { createNotification } from '../../../utils/alerts';
import { getErrorMessage } from '../../../utils/errorUtils';
import ArticleWriter from '../../../components/admin/ArticleWriter';
import NeoFeedSuggestionsPanel from '../../../components/admin/ArticleWriter/NeoFeedSuggestionsPanel';
import useNeoFeedContextSelection from '../../../components/admin/ArticleWriter/useNeoFeedContextSelection';
import { buildNeoFeedContextSourcesPayload } from '../../../components/admin/ArticleWriter/neoFeedContextUtils';

const PRIMARY = '#f25d27';

/* ─── Página: Detalhe / reescrita de uma matéria já existente ── */
export default function AdminArticleWritePage() {
    const navigate  = useNavigate();
    const { article_id } = useParams();
    const isMobile  = useMediaQuery('(max-width:900px)');

    // Histórico de versões — mais recente no índice 0
    const [articlesHistory, setArticlesHistory] = useState([]);
    const [pageLoading, setPageLoading] = useState(true);

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Áreas editoriais disponíveis para definir o tom de escrita da matéria
    const [editorialAreas, setEditorialAreas] = useState([]);
    const [editorialAreasLoading, setEditorialAreasLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const data = await getAdminEditorialAreas({ page_size: 100 });
                if (active) setEditorialAreas(data?.editorial_areas || []);
            } catch (err) {
                if (active) {
                    createNotification('error', getErrorMessage(err, 'Erro ao carregar áreas editoriais.'), isMobile);
                }
            } finally {
                if (active) setEditorialAreasLoading(false);
            }
        })();
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const loadArticle = async () => {
            setPageLoading(true);
            try {
                const data = await getAdminArticleById(article_id);
                const versions = Array.isArray(data?.versions) ? [...data.versions] : [];
                versions.sort((a, b) => new Date(b.written_at) - new Date(a.written_at));
                setArticlesHistory(versions);
            } catch (err) {
                if (err.response?.status === 404) {
                    createNotification('error', 'Matéria não encontrada.', isMobile);
                    navigate('/admin/articles');
                    return;
                } else if (err.response?.status !== 401) {
                    createNotification('error', 'Erro ao carregar matéria.', isMobile);
                }
            } finally {
                setPageLoading(false);
            }
        };
        loadArticle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [article_id]);

    const latestVersion = articlesHistory.length > 0 ? articlesHistory[0] : null;

    /* ── Sugestões NeoFeed (contexto opcional, anexável via modal de
       contexto do composer) — buscadas automaticamente a cada versão
       gerada enquanto o toggle estiver ativo. ── */
    const [neoFeedEnabled, setNeoFeedEnabled] = useState(true);
    const [neoFeedSuggestions, setNeoFeedSuggestions] = useState([]);
    const [neoFeedLoading, setNeoFeedLoading] = useState(false);
    const [neoFeedError, setNeoFeedError] = useState(null);
    const {
        selectedSources: selectedNeoFeedSources,
        toggleSuggestion: handleToggleNeoFeedSuggestion,
        removeSource: handleRemoveNeoFeedSource,
        toggleChunk: handleToggleNeoFeedChunk,
        changeUsageMode: handleChangeNeoFeedUsageMode,
        reset: resetNeoFeedSelection,
    } = useNeoFeedContextSelection({ forceUsageMode: 'factual' });

    /* ── Resetar seleção/estado do NeoFeed ao trocar de matéria (o toggle
       fica ligado por padrão) ── */
    useEffect(() => {
        setNeoFeedEnabled(true);
        setNeoFeedSuggestions([]);
        setNeoFeedError(null);
        resetNeoFeedSelection();
    }, [article_id, resetNeoFeedSelection]);

    const fetchNeoFeedSuggestions = useCallback(async () => {
        setNeoFeedLoading(true);
        setNeoFeedError(null);
        try {
            const data = await getNeoFeedContextSuggestions({ article_id });
            setNeoFeedSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
        } catch (err) {
            const message = getErrorMessage(err, 'Erro ao buscar sugestões no acervo NeoFeed.');
            setNeoFeedError(message);
            if (err.response?.status !== 401) {
                createNotification('error', message, isMobile);
            }
        } finally {
            setNeoFeedLoading(false);
        }
    }, [article_id, isMobile]);

    /* ── Como o toggle vem ligado por padrão, busca automaticamente ao
       montar/trocar de matéria — sem depender de uma ação do usuário ── */
    useEffect(() => {
        if (neoFeedEnabled) {
            fetchNeoFeedSuggestions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [neoFeedEnabled, article_id]);

    const handleToggleNeoFeedEnabled = useCallback((event) => {
        const checked = event.target.checked;
        setNeoFeedEnabled(checked);
        if (checked) {
            // A busca é disparada pelo efeito acima, reagindo à mudança de `neoFeedEnabled`.
        } else {
            setNeoFeedSuggestions([]);
            setNeoFeedError(null);
            resetNeoFeedSelection();
        }
    }, [resetNeoFeedSelection]);

    const handleGenerated = useCallback(() => {
        if (neoFeedEnabled) {
            fetchNeoFeedSuggestions();
        }
    }, [neoFeedEnabled, fetchNeoFeedSuggestions]);

    const neoFeedContextSourcesPayload = buildNeoFeedContextSourcesPayload(selectedNeoFeedSources);
    const extraPayload = neoFeedContextSourcesPayload.length
        ? { neofeed_context_sources: JSON.stringify(neoFeedContextSourcesPayload) }
        : undefined;

    const neoFeedToggleControl = (
        <Paper sx={{ borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', p: '6px 14px', mb: 1 }}>
            <FormControlLabel
                control={
                    <Switch
                        size="small"
                        checked={neoFeedEnabled}
                        onChange={handleToggleNeoFeedEnabled}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY },
                        }}
                    />
                }
                label={
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#333' }}>
                        Usar base NeoFeed
                    </Typography>
                }
                sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
            />
        </Paper>
    );

    const selectedNeoFeedCount = Object.keys(selectedNeoFeedSources).length;
    const contextSources = [
        {
            key: 'neofeed',
            label: 'Sugestões NeoFeed',
            selectedCount: selectedNeoFeedCount,
            summaryItems: Object.values(selectedNeoFeedSources)
                .map((entry) => entry.suggestion?.title)
                .filter(Boolean),
            onClearAll: resetNeoFeedSelection,
            topControl: neoFeedToggleControl,
            panel: neoFeedEnabled ? (
                <NeoFeedSuggestionsPanel
                    embedded
                    expanded
                    onToggleExpanded={() => {}}
                    suggestions={neoFeedSuggestions}
                    loading={neoFeedLoading}
                    error={neoFeedError}
                    selectedSources={selectedNeoFeedSources}
                    onToggleSuggestion={handleToggleNeoFeedSuggestion}
                    onToggleChunk={handleToggleNeoFeedChunk}
                    onChangeUsageMode={handleChangeNeoFeedUsageMode}
                    onRemoveSource={handleRemoveNeoFeedSource}
                />
            ) : (
                <Typography sx={{ fontSize: '0.82rem', color: '#999', textAlign: 'center', py: 3 }}>
                    Ative "Usar base NeoFeed" acima para ver sugestões do acervo.
                </Typography>
            ),
        },
    ];

    const streamFn = useCallback((payload, options) => (
        writeAdminArticleStream({ ...payload, article_id }, options)
    ), [article_id]);

    const handleDelete = async () => {
        setDeleteLoading(true);
        try {
            await deleteAdminArticle(article_id);
            createNotification('success', 'Matéria excluída com sucesso!', isMobile);
            navigate('/admin/articles');
        } catch (err) {
            if (err.response?.status !== 401) {
                createNotification('error', getErrorMessage(err, 'Erro ao excluir matéria.'), isMobile);
            }
        } finally {
            setDeleteLoading(false);
            setConfirmDeleteOpen(false);
        }
    };

    const extraTopActions = (
        <Button
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            onClick={() => setConfirmDeleteOpen(true)}
            sx={{
                color: '#d32f2f', textTransform: 'none', fontWeight: 500,
                '&:hover': { backgroundColor: 'rgba(211,47,47,0.06)' },
            }}
        >
            Excluir matéria
        </Button>
    );

    return (
        <>
            <ArticleWriter
                backLabel="Voltar as matérias"
                onBack={() => navigate('/admin/articles')}
                extraTopActions={extraTopActions}
                loading={pageLoading}
                resetToken={article_id}
                initialArticlesHistory={articlesHistory}
                initialArticle={latestVersion}
                initialResponseId={latestVersion?.response_id ?? null}
                streamFn={streamFn}
                onGenerated={handleGenerated}
                extraPayload={extraPayload}
                autoGenerateWhenEmpty={false}
                editorialAreas={editorialAreas}
                editorialAreasLoading={editorialAreasLoading}
                contextSources={contextSources}
            />

            {/* ── Modal: Confirmação de exclusão ───────────────────────── */}
            <Dialog
                open={confirmDeleteOpen}
                onClose={() => !deleteLoading && setConfirmDeleteOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#333' }}>
                    Confirmar exclusão
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Deseja realmente excluir a matéria <strong>{latestVersion?.title}</strong>? Esta ação não poderá ser desfeita.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setConfirmDeleteOpen(false)}
                        disabled={deleteLoading}
                        sx={{ color: '#666', textTransform: 'none' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleteLoading}
                        sx={{
                            backgroundColor: '#d32f2f', textTransform: 'none', boxShadow: 'none', borderRadius: '8px',
                            '&:hover': { backgroundColor: '#b71c1c' },
                        }}
                    >
                        {deleteLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
